import { useState } from 'react';

import { PageHeader } from '@/components/layout/PageHeader';
import { ClientSetup } from '@/components/mcp/ClientSetup';
import { GrantList } from '@/components/mcp/GrantList';
import { TokenIssueForm } from '@/components/mcp/TokenIssueForm';
import { TokenList } from '@/components/mcp/TokenList';
import { StateBlock } from '@/components/reports/StateBlock';
import { CopyButton } from '@/components/ui/copy-button';
import type { ApiToken, OAuthGrant } from '@/lib/api';
import { useApi } from '@/lib/useApi';

/**
 * MCP 接続の設定。
 *
 * トークンも接続も**その人のもの**しか出ない (発行も一覧も失効も自分の分だけ)。
 * 上から「接続先 → 接続済みアプリ → トークン → クライアント設定」の順に
 * 並べるのは、発行した平文をそのまま下の手順に差し込めるようにするため。
 *
 * **接続の方法は 2 つある。** OAuth に対応したクライアントは接続先の URL を
 * 入れるだけでよく (ブラウザが開いて許可を押す)、対応していないクライアントは
 * ここで発行したトークンを設定に貼る。前者では平文が誰の手にも渡らないので、
 * 画面でも先に置く。
 *
 * 接続先は SPA 自身のオリジンから組む。SPA と API は同一 Worker から
 * 同一オリジンで配信されるので、テナントのドメインを別途持つ必要が無い。
 */
export function McpSettingsPage() {
  const tokens = useApi<{ tokens: ApiToken[] }>('/api/tokens');
  const grants = useApi<{ grants: OAuthGrant[] }>('/api/oauth/grants');
  const [issued, setIssued] = useState<string | null>(null);
  const endpoint = `${window.location.origin}/mcp`;

  return (
    <>
      <PageHeader title="MCP接続" subtitle="AI クライアントから工数を記録するための設定" />

      <div className="flex flex-col gap-6 p-4 md:p-8">
        <div className="flex flex-col gap-4 border border-line bg-surface-raised px-6 py-5">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-bold">{'接続先'}</span>
            <CopyButton value={endpoint} />
          </div>
          <code className="block bg-surface px-4 py-3 font-mono text-sm text-ink-secondary">
            {endpoint}
          </code>
          <p className="text-[13px] leading-relaxed text-ink-muted">
            {
              'OAuth に対応した AI クライアントなら、この URL を登録するだけで接続できます。ブラウザが開くので、そこで許可を押してください。対応していないクライアントには、下で発行したトークンを設定に貼ります。'
            }
          </p>
          <p className="text-[13px] leading-relaxed text-ink-muted">
            {'いずれの方法でも、記録されるのはあなた自身の工数だけです。'}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <span className="text-sm font-bold">{'接続済みアプリ'}</span>
          <StateBlock
            loading={grants.loading && grants.data === null}
            error={grants.error}
            empty={grants.data?.grants.length === 0}
            emptyMessage="OAuth で接続したアプリはまだありません。"
          >
            <GrantList grants={grants.data?.grants ?? []} onRevoked={grants.reload} />
          </StateBlock>
        </div>

        <TokenIssueForm
          issued={issued}
          onIssued={(token) => {
            setIssued(token);
            tokens.reload();
          }}
        />

        <ClientSetup endpoint={endpoint} token={issued} />

        <StateBlock
          loading={tokens.loading && tokens.data === null}
          error={tokens.error}
          empty={tokens.data?.tokens.length === 0}
          emptyMessage="まだトークンを発行していません。"
        >
          <TokenList tokens={tokens.data?.tokens ?? []} onRevoked={tokens.reload} />
        </StateBlock>
      </div>
    </>
  );
}
